<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="transfers-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Account Transfers</h1>
          <p class="page-subtitle">Move funds between bank accounts</p>
        </div>
        <div class="header-actions">
          <IntercomButton variant="primary" @click="openCreateModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>New Transfer</span>
          </IntercomButton>
        </div>
      </div>

      <!-- Date Filters -->
      <div class="filters-row">
        <IntercomDateInput
          v-model="filterDateFrom"
          class="date-input"
          @update:model-value="applyFilters"
        />
        <span class="date-separator">to</span>
        <IntercomDateInput
          v-model="filterDateTo"
          class="date-input"
          @update:model-value="applyFilters"
        />
      </div>

      <!-- Transfers Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="items"
          :loading="loading"
          hoverable
        >
          <template #cell-transfer_date="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-amount="{ value }">
            <span class="amount-cell">{{ formatFullCurrency(value || 0) }}</span>
          </template>
          <template #cell-narration="{ value }">
            <span class="truncate">{{ value || '-' }}</span>
          </template>
          <template #cell-reference_number="{ value }">
            {{ value || '-' }}
          </template>
        </IntercomTable>
        <p v-if="!loading && items.length === 0" class="empty-text">
          No transfers found for this period.
        </p>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset.value = v; fetch() }"
      />

      <!-- Create Transfer Modal -->
      <IntercomModal v-model:show="showCreateModal" title="New Transfer">
        <form class="transfer-form" @submit.prevent="handleCreate">
          <div class="form-field">
            <label class="form-label">From Account <span class="required">*</span></label>
            <IntercomSelect
              v-model="form.from_account_id"
              :options="fromAccountOptions"
              placeholder="Select source account"
              filterable
            />
          </div>

          <div class="form-field">
            <label class="form-label">To Account <span class="required">*</span></label>
            <IntercomSelect
              v-model="form.to_account_id"
              :options="toAccountOptions"
              placeholder="Select destination account"
              filterable
            />
          </div>

          <div class="form-field">
            <label class="form-label">Amount <span class="required">*</span></label>
            <IntercomInput
              v-model="form.amount"
              type="number"
              placeholder="0.00"
              min="0.01"
              step="0.01"
              required
            />
          </div>

          <div class="form-field">
            <IntercomDateInput
              v-model="form.transfer_date"
              label="Transfer Date"
              required
            />
          </div>

          <div class="form-field">
            <label class="form-label">Narration</label>
            <IntercomInput
              v-model="form.narration"
              placeholder="e.g. Cash deposit to SBI savings"
            />
          </div>

          <div class="form-field">
            <label class="form-label">Reference Number</label>
            <IntercomInput
              v-model="form.reference_number"
              placeholder="Optional"
            />
          </div>

          <div class="form-actions">
            <IntercomButton variant="secondary" type="button" @click="closeCreateModal">
              Cancel
            </IntercomButton>
            <IntercomButton variant="primary" type="submit" :loading="isSaving">
              Create Transfer
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency, formatFullCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomModal,
  IntercomPagination,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listBankAccounts, listTransfers, createTransfer } = useSeva()
const { navSections } = useNavigation()

// Filters
const filterDateFrom = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
const filterDateTo = ref(dayjs().format('YYYY-MM-DD'))

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

// Paginated list
const {
  items,
  total,
  loading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList(
  (params) => listTransfers({
    ...params,
    date_from: filterDateFrom.value,
    date_to: filterDateTo.value,
  }),
  { immediate: false }
)

const applyFilters = () => {
  resetAndFetch({
    date_from: filterDateFrom.value,
    date_to: filterDateTo.value,
  })
}

// Table columns
const columns = [
  { key: 'transfer_number', title: 'Transfer #', width: '130px' },
  { key: 'transfer_date', title: 'Date', width: '120px' },
  { key: 'from_account_name', title: 'From Account', width: '180px' },
  { key: 'to_account_name', title: 'To Account', width: '180px' },
  { key: 'amount', title: 'Amount', width: '130px', align: 'right' },
  { key: 'narration', title: 'Narration', width: '220px' },
  { key: 'reference_number', title: 'Reference No.', width: '140px' },
]

// Bank accounts for dropdowns
const bankAccounts = ref([])

const formatAccountLabel = (a) =>
  `${a.name}${a.bank_name ? ' (' + a.bank_name + ')' : ''}${a.bank_account_number ? ' - ' + a.bank_account_number : ''}`

const fromAccountOptions = computed(() =>
  bankAccounts.value.map((a) => ({ value: a.id, label: formatAccountLabel(a) }))
)

const toAccountOptions = computed(() =>
  bankAccounts.value
    .filter((a) => a.id !== form.value.from_account_id)
    .map((a) => ({ value: a.id, label: formatAccountLabel(a) }))
)

const fetchBankAccounts = async () => {
  try {
    const response = await listBankAccounts()
    bankAccounts.value = response?.accounts || response?.bank_accounts || []
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
  }
}

// Modal + form state
const showCreateModal = ref(false)
const isSaving = ref(false)

const emptyForm = () => ({
  from_account_id: null,
  to_account_id: null,
  amount: '',
  transfer_date: dayjs().format('YYYY-MM-DD'),
  narration: '',
  reference_number: '',
})

const form = ref(emptyForm())

const openCreateModal = () => {
  form.value = emptyForm()
  showCreateModal.value = true
}

const closeCreateModal = () => {
  showCreateModal.value = false
}

const handleCreate = async () => {
  if (!form.value.from_account_id) {
    toast.error('Please select a source account.')
    return
  }
  if (!form.value.to_account_id) {
    toast.error('Please select a destination account.')
    return
  }
  if (form.value.from_account_id === form.value.to_account_id) {
    toast.error('Source and destination accounts must be different.')
    return
  }
  if (!form.value.amount || Number(form.value.amount) <= 0) {
    toast.error('Please enter a valid amount.')
    return
  }

  isSaving.value = true
  try {
    await createTransfer({
      from_account_id: form.value.from_account_id,
      to_account_id: form.value.to_account_id,
      amount: Number(form.value.amount),
      transfer_date: form.value.transfer_date,
      narration: form.value.narration || undefined,
      reference_number: form.value.reference_number || undefined,
    })
    toast.success('Transfer created successfully.')
    closeCreateModal()
    fetch()
  } catch (error) {
    console.error('Failed to create transfer:', error)
    toast.error(error.detail || error.message || 'Failed to create transfer.')
  } finally {
    isSaving.value = false
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchBankAccounts()
  fetch()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.transfers-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: center;
}

.date-input {
  width: 150px;
}

.date-separator {
  color: var(--color-gray-500);
}

.amount-cell {
  font-variant-numeric: tabular-nums;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

/* Transfer Form */
.transfer-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.required {
  color: var(--color-red-500);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-2);
}

@media (max-width: 768px) {
  .transfers-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .header-actions {
    width: 100%;
  }

  .filters-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .date-input {
    width: 100%;
  }
}
</style>
